/**
 * `POST /api/cart/sync-logout` — re-tag a logged-out user's cart rows under
 * their guest id so the cart persists across logout. Called by the cart
 * provider AFTER `supabase.auth.signOut()` — the session cookie is typically
 * already gone, so the route falls back to the client-supplied `userId`.
 *
 * Fire-and-forget at the call site (timeout 10s, errors logged but ignored —
 * worst case the buyer logs back in and their cart is restored from the
 * server anyway). Response is parsed only for schema-drift detection.
 *
 * @see app/api/cart/sync-logout/route.ts — handler
 * @see lib/cart/sync-logout-cart.ts — builder
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface CartSyncLogoutRequest {
  /** The user that just signed out. Required (session cookie is already gone). */
  userId: string;
  /** Guest id to re-tag the rows under. Required. */
  guestId: string;
}

export interface CartSyncLogoutResponse {
  success: true;
}

export const CartSyncLogoutRequestSchema = pinned<CartSyncLogoutRequest>()(
  z.object({
    userId: z.string().min(1),
    guestId: z.string().min(1),
  }),
);

export const CartSyncLogoutResponseSchema = pinned<CartSyncLogoutResponse>()(
  z.object({
    success: z.literal(true),
  }),
);
